import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiGallection, PapiPremiumFolderData } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EyeDisc2 from '@wix/da-ds/pkg/Icons/24x24/EyeDisc2';
import PremiumGallery from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import WatchButton from '@wix/da-shared-react/pkg/Button/ProfileOwnerWatchButton';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import { LoggedOutReturnType } from '@wix/da-shared-react/pkg/redux/loggedout';
import DiscountLabel from '@wix/da-shared-react/pkg/DiscountLabel';
import FolderPaywallLayout from './FolderPaywallLayout';
import PremiumGalleryPaywallLayout from './PremiumGalleryPaywallLayout';

import s from './FolderPaywall.scss';

export interface Props {
  folder: PapiGallection;
  premiumFolderData: PapiPremiumFolderData;
  biData?: BiEvent;
  isOwner: boolean;
}

const FolderPaywall: FC<Props> = ({ folder, biData, premiumFolderData }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { restrictToLoggedIn } = useRestrictToLoggedIn();

  const {
    formattedDollarPriceAfterDiscount,
    isPaidFolder,
    isWatchToAccess,
    eligibleDiscount,
    subproductId,
  } = normalizePremiumFolderData(folder.premiumFolderData);
  const contentOwner = folder.owner;
  const checkoutLink = subproductId
    ? Url.checkoutLink(subproductId)
    : undefined;

  if (isPaidFolder) {
    return (
      <PremiumGalleryPaywallLayout
        folder={folder}
        premiumFolderData={premiumFolderData}
        icon={<PremiumGallery size={IconSize.MEDIUM} />}
        button={
          <BuyButton
            className={s['unlock-button']}
            variant="solid"
            size={isMobile ? 'large' : 'medium'}
            icon={PremiumGallery}
            biData={biData}
            onClick={restrictToLoggedIn(
              ReferralType.SHOP_PREMIUM_GALLERIES,
              () => {
                if (checkoutLink) window.location.assign(checkoutLink);
              },
              checkoutLink,
              LoggedOutReturnType.JOIN_ONLY
            )}
            rounded
          >
            {t('paywall.premium.folder.unlockFor')}{' '}
            {formattedDollarPriceAfterDiscount}{' '}
            <DiscountLabel
              className={s['buy-button-discount']}
              discount={eligibleDiscount}
              textVariant="percent"
              reversedTheme
            />
          </BuyButton>
        }
      />
    );
  }

  if (isWatchToAccess) {
    return (
      <FolderPaywallLayout
        premiumFolderData={premiumFolderData}
        folder={folder}
        icon={<EyeDisc2 size={IconSize.MEDIUM} />}
        button={
          <WatchButton
            user={contentOwner!}
            onToggleWatchSuccess={() => {
              window.location.href = premiumFolderData.galleryUrl!;
            }}
          />
        }
      />
    );
  }

  return null;
};

FolderPaywall.displayName = 'FolderPaywall';

export default FolderPaywall;
